import React, { useState } from 'react'
import { IoMdStopwatch } from 'react-icons/io'
import { IoPlaySharp, IoStopSharp } from 'react-icons/io5'
import { useTimer } from '../../../hooks/useTimer'
import { useTimerData } from '../../../providers/TimerDataProvider'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/modules/rootReducer'
import { planningApi } from '../../../pages/Planning/services/api'
import StopModal from '../StopModal'
import { StartButton } from './styles'

interface IStartTimerButtonProps {
  compact?: boolean
}

/**
 * Botão para iniciar o timer da sessão atual
 * Pode ser usado em qualquer lugar da aplicação
 * Geralmente colocado na tela de planejamento
 */
const StartTimerButton: React.FC<IStartTimerButtonProps> = ({ compact = false }) => {
  const { startTimer, resumeTimer, currentSession, status, setVisibility } = useTimer()
  const { cycleData } = useTimerData()
  const currentPlanId = useSelector((state: RootState) => state.studyPlan.currentPlanId)
  const [isStopModalOpen, setIsStopModalOpen] = useState(false)

  // Se não há sessão atual, não exibe o botão
  if (!currentSession) {
    return null
  }

  const handleStart = async () => {
    // Inicia o timer
    startTimer()
    setVisibility(true)

    // Atualiza status para 'in_progress' se estiver 'pending'
    if (currentPlanId && currentSession.id) {
      const currentCycleSession = cycleData?.sessions?.find((s) => s._id === currentSession.id)

      if (currentCycleSession?.status === 'pending') {
        try {
          await planningApi.patchCycleSession(currentPlanId, currentSession.id, 'in_progress')
          // Atualização silenciosa - sem toast nem reload
        } catch (err: any) {
          // Falha silenciosamente - não mostra erro ao usuário
          console.error('Erro ao atualizar status ao iniciar temporizador:', err)
        }
      }
    }
  }

  const handleResume = () => {
    if (currentSession && status === 'paused') {
      resumeTimer()
    }
  }

  const handleStop = () => {
    if (currentSession && status === 'running') {
      setIsStopModalOpen(true)
    }
  }

  const handleCloseStopModal = () => {
    setIsStopModalOpen(false)
  }

  // Renderiza botão baseado no status
  const renderButton = () => {
    if (status === 'running') {
      return (
        <StartButton onClick={handleStop} compact={compact}>
          <IoStopSharp />
          {!compact && <span>Parar</span>}
        </StartButton>
      )
    }

    if (status === 'paused') {
      return (
        <StartButton onClick={handleResume} compact={compact}>
          <IoPlaySharp />
          {!compact && <span>Retomar</span>}
        </StartButton>
      )
    }

    // Status 'idle' ou 'stopped'
    return (
      <StartButton onClick={handleStart} compact={compact}>
        <IoMdStopwatch />
        {!compact && <span>Iniciar cronômetro</span>}
      </StartButton>
    )
  }

  return (
    <>
      {renderButton()}
      <StopModal isOpen={isStopModalOpen} onClose={handleCloseStopModal} />
    </>
  )
}

export default StartTimerButton
